import { simpleEmployee } from '@/mocks/data/employeeData';
import { simpleOrganization } from '@/mocks/data/organizationData';
import { simpleUser } from '@/mocks/data/userData';
import {
  DisabledReason,
  ScheduledTask,
  TaskRunOutcome,
} from '@/types/scheduledTasks';

export const SCHEDULED_TASK_USER_LIMIT = 10;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const daysAgo = (days: number) =>
  new Date(Date.now() - days * MS_PER_DAY).toISOString();
const daysFromNow = (days: number) =>
  new Date(Date.now() + days * MS_PER_DAY).toISOString();

const baseTask = {
  organization_id: simpleOrganization.id,
  disabled_reason: null,
  last_run_at: null,
  next_due_at: null,
  run_history: [],
} satisfies Partial<ScheduledTask>;

export const buildMockScheduledTasks = (): ScheduledTask[] => [
  {
    ...baseTask,
    user_id: simpleUser.id,
    schedule_id: 'schedule-active-mixed-history',
    prompt: 'Summarize devices that have not checked in for 7 days.',
    cadence: '0 9 * * 1',
    timezone: 'UTC',
    cadence_description: 'Every Monday at 9:00 AM',
    enabled: true,
    creator_email: simpleUser.email,
    recipient_employee_ids: [simpleEmployee.id, 'bulk-mock-employee-1'],
    created_at: daysAgo(30),
    last_run_at: daysAgo(1),
    next_due_at: daysFromNow(6),
    run_history: [
      {
        started_at: daysAgo(1),
        outcome: TaskRunOutcome.Sent,
        reason: null,
        skipped_occurrences: 0,
      },
      {
        started_at: daysAgo(8),
        outcome: TaskRunOutcome.Skipped,
        reason: 'Previous run was still in progress',
        skipped_occurrences: 2,
      },
      {
        started_at: daysAgo(15),
        outcome: TaskRunOutcome.Failed,
        reason: 'Email delivery failed for it-team@simple-org.com',
        skipped_occurrences: 0,
      },
    ],
  },
  {
    ...baseTask,
    user_id: simpleUser.id,
    schedule_id: 'schedule-empty-history',
    prompt: 'List employees whose onboarding is still incomplete.',
    cadence: '0 8 * * *',
    timezone: 'UTC',
    cadence_description: 'Every day at 8:00 AM',
    enabled: true,
    creator_email: simpleUser.email,
    recipient_employee_ids: [simpleEmployee.id],
    created_at: daysAgo(0),
    next_due_at: daysFromNow(1),
  },
  {
    // Cadence outside the builder grammar (e.g. created by the chat agent),
    // to exercise the "unsupported schedule" UI state.
    ...baseTask,
    user_id: simpleUser.id,
    schedule_id: 'schedule-unsupported-cadence',
    prompt: 'Check for devices with low disk space.',
    cadence: '*/30 * * * *',
    timezone: 'UTC',
    cadence_description: 'Every 30 minutes',
    enabled: true,
    creator_email: simpleUser.email,
    recipient_employee_ids: [simpleEmployee.id],
    created_at: daysAgo(2),
    next_due_at: daysFromNow(0),
  },
  {
    // Owned by another admin whose account is no longer active, so the task was
    // disabled; any admin can still inspect, edit and delete it.
    ...baseTask,
    user_id: 'other-user-id',
    schedule_id: 'schedule-creator-inactive',
    prompt: 'Report on unused application licenses.',
    cadence: '0 7 1 * *',
    timezone: 'UTC',
    cadence_description: 'On the 1st of every month at 7:00 AM',
    enabled: false,
    disabled_reason: DisabledReason.CreatorInactive,
    creator_email: 'former-admin@simple-org.com',
    recipient_employee_ids: ['bulk-mock-employee-1'],
    created_at: daysAgo(90),
    last_run_at: daysAgo(40),
  },
];
